/**
 * Cookie utility to replace redux-effects-cookie
 * Returns a thunk action that can be dispatched
 */

// Get a cookie value. When duplicates exist (e.g. the same name was previously
// set without an explicit path and again with path=/), browsers list the more
// specific entry first; we return the first match so the freshest value wins.
export const getCookie = (name) => {
  if (typeof document === 'undefined') return undefined
  const cookies = document.cookie ? document.cookie.split('; ') : []
  const match = cookies.find((entry) => {
    const eq = entry.indexOf('=')
    return (eq === -1 ? entry : entry.slice(0, eq)) === name
  })
  if (!match) return undefined
  const eq = match.indexOf('=')
  return eq === -1 ? '' : match.slice(eq + 1)
}

// Set a cookie value
export const setCookie = (name, value, options = {}) => {
  if (typeof document === 'undefined') return

  let cookieString = `${name}=${value}`

  if (options.maxage) {
    const date = new Date()
    date.setTime(date.getTime() + options.maxage)
    cookieString += `; expires=${date.toUTCString()}`
  }

  if (options.path) cookieString += `; path=${options.path}`
  if (options.domain) cookieString += `; domain=${options.domain}`
  if (options.samesite) cookieString += `; samesite=${options.samesite}`
  if (options.secure) cookieString += '; secure'

  document.cookie = cookieString
}

// Delete a cookie
export const deleteCookie = (name, options = {}) => {
  if (typeof document === 'undefined') return
  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT`
  if (options.path) cookieString += `; path=${options.path}`
  if (options.domain) cookieString += `; domain=${options.domain}`
  document.cookie = cookieString
}

/**
 * Cookie action creator (thunk)
 * @param {string} name - Cookie name
 * @param {*} value - Optional value to set (undefined = read, null = delete)
 * @param {object} options - Cookie options (maxage, path)
 * @returns {function} Thunk that returns a promise
 */
export const cookie = (name, value, options = {}) => () =>
  new Promise((resolve) => {
    if (value === undefined)
      // Read cookie
      resolve(getCookie(name))
    else if (value === null) {
      // Delete cookie
      deleteCookie(name, options)
      resolve()
    } else {
      // Set cookie
      setCookie(name, value, options)
      resolve()
    }
  })

export default cookie
